import { and, eq } from "drizzle-orm";
import {
  aisle,
  pantry_item,
  type PowerSyncDrizzleDB,
  shopping_item,
  shopping_list,
  shopping_list_organization,
} from "@mealplan/powersync";
import { nowISO } from "../utils/date-helpers";
import {
  patchRowVerified,
  readMatchingRowVerified,
  type VerifiedWriteOptions,
} from "../powersync/verified-write";
import { generateId } from "@fleet/utils";

/**
 * Returns a name→aisle_id map, creating any missing aisles.
 * Used by the recipe editor: the user types the category as free text
 * and here it is materialized into the user's `aisle` catalogue.
 *
 * INSERT-vs-reuse is decided by CONTENT (aisle name), not by a known `id`
 * — Class 2 of `../powersync/verified-write.ts`. The bulk `SELECT`
 * above can land just when a freshly created aisle (for example from
 * saving ANOTHER recipe moments earlier) has not yet appeared in the
 * local checkpoint; for names that `SELECT` does not find, they are re-read
 * one by one with `readMatchingRowVerified` (retry with backoff) BEFORE
 * deciding they must be created — and IN PARALLEL across names, so a
 * genuinely new aisle does not pay the latency of another that did need
 * to retry. If the backoff is exhausted without finding it, it is created:
 * this is the first time that name is touched, a legitimate result, not a
 * failure.
 */
export async function ensureAislesByNameMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  names: string[],
  options: VerifiedWriteOptions = {},
): Promise<Map<string, string>> {
  const wanted = [...new Set(names.map((n) => n.trim()).filter(Boolean))];
  const result = new Map<string, string>();
  if (wanted.length === 0) {
    return result;
  }

  const existing = await db.select().from(aisle).where(eq(aisle.user_id, userId));
  const byLowerName = new Map(
    existing.map((row) => [(row.name ?? "").trim().toLowerCase(), row] as const),
  );

  const missingNames = wanted.filter((name) => !byLowerName.has(name.toLowerCase()));
  const rechecked = await Promise.all(
    missingNames.map(async (name) => {
      const row = await readMatchingRowVerified(async () => {
        const rows = await db.select().from(aisle).where(eq(aisle.user_id, userId));
        return rows.filter((r) => (r.name ?? "").trim().toLowerCase() === name.toLowerCase());
      }, options);
      return [name, row] as const;
    }),
  );
  for (const [name, row] of rechecked) {
    if (row) {
      byLowerName.set(name.toLowerCase(), row);
    }
  }

  const now = nowISO();
  let nextOrder = existing.length;
  for (const name of wanted) {
    const found = byLowerName.get(name.toLowerCase());
    if (found) {
      result.set(name, found.id);
      continue;
    }
    const id = generateId();
    await db.insert(aisle).values({
      id,
      user_id: userId,
      name,
      sort_order: nextOrder,
      created_at: now,
      updated_at: now,
    });
    nextOrder += 1;
    result.set(name, id);
  }
  return result;
}

export async function createShoppingListMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  name: string,
  weekStart?: string | null,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(shopping_list).values({
    id,
    user_id: userId,
    name: name.trim(),
    week_start: weekStart ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function deleteShoppingListMutation(
  db: PowerSyncDrizzleDB,
  listId: string,
): Promise<void> {
  await db.transaction(async (trx) => {
    await trx.delete(shopping_item).where(eq(shopping_item.shopping_list_id, listId));
    await trx
      .delete(shopping_list_organization)
      .where(eq(shopping_list_organization.shopping_list_id, listId));
    await trx.delete(shopping_list).where(eq(shopping_list.id, listId));
  });
}

/**
 * Adds a manual item. `ownerId` must be the user_id of the list's OWNER
 * (denormalization of the sync model — the caller reads it from the list itself).
 */
export async function addManualItemMutation(
  db: PowerSyncDrizzleDB,
  ownerId: string,
  listId: string,
  input: {
    name: string;
    quantity?: number | null;
    unit?: string | null;
    category?: string | null;
  },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(shopping_item).values({
    id,
    shopping_list_id: listId,
    user_id: ownerId,
    name: input.name.trim(),
    quantity: input.quantity ?? null,
    unit: input.unit?.trim() || null,
    category: input.category?.trim() || null,
    checked: 0,
    is_manual: 1,
    sort_order: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function setItemCheckedMutation(
  db: PowerSyncDrizzleDB,
  itemId: string,
  checked: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    shopping_item,
    shopping_item.id,
    itemId,
    { checked: checked ? 1 : 0 },
    options,
  );
}

// ⚠️ No callers in apps/ (dead domain, no live routes use it): it is NOT
// wrapped with `patchRowVerified` on purpose — there is nothing to protect. If it
// gains a caller in the future, wrap it the same way as `setItemCheckedMutation`.
export async function updateShoppingItemMutation(
  db: PowerSyncDrizzleDB,
  itemId: string,
  updates: Partial<{
    name: string;
    quantity: number | null;
    unit: string | null;
    category: string | null;
  }>,
): Promise<void> {
  const finalUpdates = { ...updates };
  if (finalUpdates.name !== undefined) {
    finalUpdates.name = finalUpdates.name.trim();
  }
  await db
    .update(shopping_item)
    .set({ ...finalUpdates, updated_at: nowISO() })
    .where(eq(shopping_item.id, itemId));
}

export async function deleteShoppingItemMutation(
  db: PowerSyncDrizzleDB,
  itemId: string,
): Promise<void> {
  await db.delete(shopping_item).where(eq(shopping_item.id, itemId));
}

/** Removes the already-checked items from a list. */
export async function clearCheckedItemsMutation(
  db: PowerSyncDrizzleDB,
  listId: string,
): Promise<void> {
  await db
    .delete(shopping_item)
    .where(and(eq(shopping_item.shopping_list_id, listId), eq(shopping_item.checked, 1)));
}

/** Shares the list with an organization (household). */
export async function shareShoppingListMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  listId: string,
  organizationId: string,
): Promise<void> {
  await db.insert(shopping_list_organization).values({
    id: generateId(),
    shopping_list_id: listId,
    organization_id: organizationId,
    added_at: nowISO(),
    added_by: userId,
  });
}

/** Revokes the list share with an organization. */
export async function revokeShoppingListMutation(
  db: PowerSyncDrizzleDB,
  listId: string,
  organizationId: string,
): Promise<void> {
  await db
    .delete(shopping_list_organization)
    .where(
      and(
        eq(shopping_list_organization.shopping_list_id, listId),
        eq(shopping_list_organization.organization_id, organizationId),
      ),
    );
}

// ─── Pantry ──────────────────────────────────────────────────────────────────

export async function addPantryItemMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  input: { name: string; quantity?: number | null; unit?: string | null },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(pantry_item).values({
    id,
    user_id: userId,
    name: input.name.trim(),
    quantity: input.quantity ?? null,
    unit: input.unit?.trim() || null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function deletePantryItemMutation(
  db: PowerSyncDrizzleDB,
  itemId: string,
): Promise<void> {
  await db.delete(pantry_item).where(eq(pantry_item.id, itemId));
}
